import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Chip,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Drawer,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { useCalculatorStore } from './store/calculatorStore';
import { useCalculator, useApplyQuestionnaire } from './hooks/useCalculator';
import { useConfig } from './hooks/useConfig';
import { ProjectSelector } from './components/features/ProjectSelector';
import { QuestionnaireRenderer } from './components/renderers/QuestionnaireRenderer';
import { TotalCostHeader } from './components/layout/TotalCostHeader';
import { CalculatorView } from './components/CalculatorView';
import { CalculatorSidebar } from './components/renderers/CalculatorSidebar';
import { projectLogo } from './utils/projectLogos';

// Kept in one place so CalculatorView's toolbar and the Drawer stay in sync.
const SIDEBAR_W = 340;
// Rough height of AppBar (64) + TotalCostHeader (~96) — the Drawer paper
// starts below them so both stay visible.
const SIDEBAR_TOP_OFFSET = 160;

function App() {
  const step = useCalculatorStore((s) => s.step);
  const breakdown = useCalculatorStore((s) => s.breakdown);
  const schema = useCalculatorStore((s) => s.schema);
  const selectProject = useCalculatorStore((s) => s.selectProject);
  const setAnswers = useCalculatorStore((s) => s.setAnswers);
  const setParams = useCalculatorStore((s) => s.setParams);
  const setStep = useCalculatorStore((s) => s.setStep);
  const configName = useCalculatorStore((s) => s.configName);
  const setConfigName = useCalculatorStore((s) => s.setConfigName);
  const sidebarOpen = useCalculatorStore((s) => s.sidebarOpen);

  const { calculateAll } = useCalculator();
  const { loadFromFile, error: loadError } = useConfig();
  const applyQuestionnaire = useApplyQuestionnaire();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const totals = useMemo(() => {
    if (breakdown) {
      return {
        monthly: breakdown.total_monthly,
        daily: breakdown.total_daily,
        yearly: breakdown.total_yearly,
      };
    }
    return null;
  }, [breakdown]);

  // Deep-link: ?project=foo — just preselects the project. Saved files
  // are user-owned so ?config= is no longer supported.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const projectParam = urlParams.get('project');
    if (projectParam && step === 'project_selection') {
      selectProject(projectParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 'calculator') {
      calculateAll();
    }
  }, [step, calculateAll]);

  const handleQuestionnaireSubmit = async (answers: Record<string, unknown>) => {
    setAnswers(answers);
    try {
      const resolved = await applyQuestionnaire(answers);
      setParams(resolved);
    } catch {
      setParams(answers);
    }
    setStep('calculator');
  };

  const handleQuestionnaireSkip = async () => {
    if (!schema) return;
    // Skip uses the schema's questionnaire defaults, but the questionnaire
    // shape != params shape (e.g. text_questions_per_user_per_day vs
    // text_msgs_per_user_per_day) — route through apply-questionnaire so
    // the transforms that the submit path does also run here.
    const defaults = { ...schema.questionnaire.defaults };
    setAnswers(defaults);
    try {
      const resolved = await applyQuestionnaire(defaults);
      setParams(resolved);
    } catch {
      setParams(defaults);
    }
    setStep('calculator');
  };

  // --- Upload handler ------------------------------------------------------

  const triggerUpload = () => fileInputRef.current?.click();

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected after an error.
    e.target.value = '';
    if (!file) return;
    const cfg = await loadFromFile(file);
    if (cfg) setToast(`Loaded "${cfg.name}"`);
  };

  // --- Rename pill ---------------------------------------------------------

  const startRename = () => {
    setRenameDraft(configName || '');
    setRenaming(true);
  };
  const commitRename = () => {
    const next = renameDraft.trim();
    if (next) setConfigName(next);
    setRenaming(false);
  };
  const cancelRename = () => setRenaming(false);

  const reset = useCalculatorStore((s) => s.reset);
  const goHome = () => {
    setRenaming(false);
    reset();
  };

  const appTitle = schema?.project.name ?? 'Cost Calculator';
  // Once a project is chosen its wordmark replaces the plain title, so the
  // header does not repeat the product name twice.
  const headerLogo = projectLogo(schema?.project.id);
  const footerText = schema
    ? `Cloud Cost Calculator for ${schema.project.name}`
    : 'Cloud Cost Calculator';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="static"
        elevation={0}
        color="default"
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          {/* Logo + title act as a home link. */}
          <Box
            onClick={goHome}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') goHome();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexGrow: 1,
              cursor: 'pointer',
            }}
            aria-label="Go to home"
          >
            <CalculateIcon color="primary" />
            {headerLogo ? (
              <Box
                component="img"
                src={headerLogo}
                alt={appTitle}
                sx={{ height: 26, display: 'block' }}
              />
            ) : (
              <Typography variant="h6" component="div">
                {appTitle}
              </Typography>
            )}
          </Box>

          {/* Right-side slot: Upload on home; name pill on calculator. */}
          {step === 'project_selection' && (
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={triggerUpload}
            >
              Upload
            </Button>
          )}

          {step === 'calculator' && configName && !renaming && (
            <Chip
              label={configName}
              onClick={startRename}
              onDelete={startRename}
              deleteIcon={<EditIcon />}
              size="small"
              sx={{
                bgcolor: 'action.selected',
                color: 'inherit',
                '& .MuiChip-deleteIcon': { color: 'inherit' },
              }}
            />
          )}
          {step === 'calculator' && renaming && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TextField
                autoFocus
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') cancelRename();
                }}
                size="small"
                variant="standard"
                sx={{ '& .MuiInputBase-input': { color: 'inherit' } }}
              />
              <IconButton size="small" onClick={commitRename} sx={{ color: 'inherit' }}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={cancelRename} sx={{ color: 'inherit' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Toolbar>

        {/* Hidden file input driven by the Upload button. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json,.json"
          hidden
          onChange={handleFileChosen}
        />
      </AppBar>

      {/* Print-only header. Rendered above TotalCostHeader so it lands
          at the very top of the printed first page. */}
      {step === 'calculator' && schema && (
        <Box
          className="print-only print-header"
          component="header"
          sx={{ px: 3 }}
        >
          <h1>{schema.project.name}</h1>
          {configName && <p className="print-config-name">{configName}</p>}
          <p className="print-subtitle">Cloud Cost Estimate</p>
          <p className="print-meta">Generated: {new Date().toLocaleString()}</p>
        </Box>
      )}

      {step === 'calculator' && totals && (
        <TotalCostHeader
          totalMonthly={totals.monthly}
          totalDaily={totals.daily}
          totalYearly={totals.yearly}
        />
      )}

      {/* Main content shifts right by the sidebar's width when open so
          the sidebar doesn't cover any content. `main-content` class
          lets print.css reset the push during PDF capture so the
          output is centre-aligned. */}
      <Box
        className="main-content"
        sx={{
          flexGrow: 1,
          transition: (theme) =>
            theme.transitions.create('padding-left', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          pl: step === 'calculator' && sidebarOpen ? `${SIDEBAR_W}px` : 0,
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {step === 'project_selection' && <ProjectSelector />}
          {step === 'questionnaire' && schema && (
            <QuestionnaireRenderer
              schema={schema}
              onSubmit={handleQuestionnaireSubmit}
              onSkip={handleQuestionnaireSkip}
            />
          )}
          {step === 'calculator' && <CalculatorView />}
        </Container>
      </Box>

      {/* Adjustable-inputs sidebar. Persistent drawer slides in from
          the left and stays open; content to the right is pushed via
          the padding-left transition above. */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={step === 'calculator' && sidebarOpen}
        className="no-print"
        sx={{
          width: SIDEBAR_W,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_W,
            top: SIDEBAR_TOP_OFFSET,
            height: `calc(100vh - ${SIDEBAR_TOP_OFFSET}px)`,
            boxSizing: 'border-box',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <CalculatorSidebar />
      </Drawer>

      <Box
        component="footer"
        className="no-print"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            {footerText}
          </Typography>
        </Container>
      </Box>

      {/* Upload result toast / error. */}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={toast}
      />
      <Snackbar
        open={Boolean(loadError)}
        autoHideDuration={5000}
        onClose={() => { /* error message is read from hook; clears on next op */ }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }}>
          {loadError}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default App;
